package com.evoapps.evoshift.ui.courses

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.GroupAdd
import androidx.compose.material.icons.filled.PersonAdd
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.CourseProgressRow
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.documents.formatMoment
import com.evoapps.evoshift.ui.user.formatDay
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.CourseProgressViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoCheckboxRow
import com.evoapps.evoshift.widgets.EvoDateField
import com.evoapps.evoshift.widgets.MissingYear
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTopBar
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.course_assign
import evoshift.composeapp.generated.resources.course_assign_action
import evoshift.composeapp.generated.resources.course_assign_due
import evoshift.composeapp.generated.resources.course_assign_people
import evoshift.composeapp.generated.resources.course_due_on
import evoshift.composeapp.generated.resources.course_failed_attempts
import evoshift.composeapp.generated.resources.course_former
import evoshift.composeapp.generated.resources.course_not_started
import evoshift.composeapp.generated.resources.course_overdue_since
import evoshift.composeapp.generated.resources.course_pages_seen
import evoshift.composeapp.generated.resources.course_passed_on
import evoshift.composeapp.generated.resources.course_progress_done
import evoshift.composeapp.generated.resources.course_progress_empty
import evoshift.composeapp.generated.resources.course_progress_title
import evoshift.composeapp.generated.resources.ohs_open_record
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

fun progressRowTag(userId: String) = "progress_row_$userId"

/**
 * SH-126: who a course is for (personally, by position, as a new hire) and how far each person
 * got: passed with what score and when, failed attempts, pages seen, overdue. Managers assign
 * people personally with a due day; for OHS courses each row opens the person's OHS record.
 */
@Composable
fun CourseProgressScreen(viewModel: CourseProgressViewModel, onNavigateBack: () -> Unit, onOpenOhs: (userId: String) -> Unit) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val shown = (state.error ?: state.message)?.asString()
    LaunchedEffect(shown) {
        shown?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    val report = state.report

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.course_progress_title), onBack = onNavigateBack) }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize().contentWidth(ContentMaxWidth)) {
            if (state.isLoading || report == null) {
                if (state.isLoading) CircularProgressIndicator(Modifier.align(Alignment.Center))
                return@Box
            }
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(report.course.title, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                Text(stringResource(Res.string.course_progress_done, report.done, report.total))
                if (state.canManage) {
                    Button(onClick = viewModel::openAssign) {
                        Icon(Icons.Default.PersonAdd, contentDescription = null)
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.course_assign))
                    }
                }
                if (report.rows.isEmpty()) {
                    Text(stringResource(Res.string.course_progress_empty), color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
                report.rows.forEach { row ->
                    HorizontalDivider()
                    ProgressRow(
                        row = row,
                        pageCount = report.course.pages.size,
                        today = state.today,
                        onOhs = if (report.course.kind.isOhs && !row.former) { { onOpenOhs(row.userId) } } else null
                    )
                }
            }
        }
    }

    if (state.assigning) {
        EvoDialog(
            title = stringResource(Res.string.course_assign),
            icon = Icons.Default.GroupAdd,
            onDismiss = viewModel::closeAssign,
            confirm = DialogAction(stringResource(Res.string.course_assign_action), viewModel::assign, enabled = state.selected.isNotEmpty())
        ) {
            DialogSection(stringResource(Res.string.course_assign_people)) {
                state.team.forEach { (uid, name) ->
                    EvoCheckboxRow(checked = uid in state.selected, onCheckedChange = { viewModel.toggleSelected(uid) }, label = name)
                }
            }
            EvoDateField(value = state.dueOn, onValueChange = viewModel::setDueOn, label = stringResource(Res.string.course_assign_due), missingYear = MissingYear.Upcoming)
        }
    }
}

@Composable
private fun ProgressRow(row: CourseProgressRow, pageCount: Int, today: LocalDate, onOhs: (() -> Unit)?) {
    Row(Modifier.fillMaxWidth().testTag(progressRowTag(row.userId)), verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(row.displayName + if (row.former) " · " + stringResource(Res.string.course_former) else "", fontWeight = FontWeight.Bold)
            val completion = row.completion
            when {
                completion?.passed == true -> Text(
                    stringResource(Res.string.course_passed_on, completion.completedAt?.let { formatMoment(it) }.orEmpty(), completion.score),
                    color = TrainingDoneColor
                )
                completion != null -> Text(stringResource(Res.string.course_failed_attempts, completion.attempts, completion.score))
                row.pagesSeen > 0 -> Text(stringResource(Res.string.course_pages_seen, row.pagesSeen, pageCount))
                else -> Text(stringResource(Res.string.course_not_started), color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
            row.dueOn?.let { due ->
                if (row.overdueOn(today)) Text(stringResource(Res.string.course_overdue_since, formatDay(due)), color = TrainingOverdueColor, fontWeight = FontWeight.Bold)
                else if (!row.passed) Text(stringResource(Res.string.course_due_on, formatDay(due)), style = MaterialTheme.typography.bodySmall)
            }
        }
        if (onOhs != null) TextButton(onClick = onOhs) { Text(stringResource(Res.string.ohs_open_record)) }
    }
}
